import { CheckCircleOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetDriversQuery } from "src/_api/driverApi";
import { useGetWarehousesQuery } from "src/_api/warehouseApi";
import { InputExcelFile } from "src/_modules/common";
import { $BASE_URL, FEDEX_IDS, ROLES } from "src/constants";

export default function ImportOrdersDialog({
  open,
  error,
  isSuccess,
  loading,
  onClose,
  onConfirm,
  importedOrdersStatus,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.entities.profile);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [base64, setBase64] = useState(null);

  const { data: warehouses, isLoading: warehousesLoading } =
    useGetWarehousesQuery({ limit: 100 });
  const { data: drivers, isLoading: driversLoading } = useGetDriversQuery({
    limit: 100,
  });

  const handleChange = ({ target: { value } }) => {
    setBase64(value);
  };

  const closeDialogHandler = () => {
    onClose();
    setBase64(null);
    setSelectedWarehouse(null);
    setSelectedDriver(null);
  };

  const confirmHandler = () => {
    onConfirm({
      base64,
      driver: selectedDriver?.id,
      shippingCompanyWarehouse: selectedWarehouse?.id,
    });
  };

  const disabledConfirm = useMemo(() => {
    if (isSuccess === true) return true;
    if (!!base64 && !!selectedWarehouse) {
      if (
        FEDEX_IDS.includes(profile?.merchant?.id) &&
        !Boolean(selectedDriver)
      ) {
        return true;
      }
      return false;
    }
    return true;
  }, [base64, selectedWarehouse, isSuccess, selectedDriver]);

  useEffect(() => {
    if (isSuccess && !importedOrdersStatus?.errors?.length) {
      setTimeout(() => {
        closeDialogHandler();
        navigate(
          "/merchant/tasks/tasks_count?status=TASK_ASSIGNED_TO_DRIVER_FOR_DROP_OFF",
        );
      }, 2500);
    }
  }, [isSuccess]);

  return (
    <Dialog open={open} onClose={(e) => e.preventDefault()} fullWidth>
      <DialogTitle>{t("importOrdersDialog.title")}</DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="subtitle1">
          {t("importOrdersDialog.message")}
        </Typography>
        {!FEDEX_IDS.includes(profile?.merchant?.id) && (
          <Box
            sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}
          >
            <Typography variant="subtitle1">
              {t("importOrdersDialog.excelSchemaPropmt")}
            </Typography>
            <Button
              disabled={false}
              color="main"
              sx={{ textTransform: "none" }}
              href={`${$BASE_URL}/mapit_excel_schema-for-orders.xls`}
            >
              {t("importOrdersDialog.clickHere")}
            </Button>
          </Box>
        )}
        <Box
          sx={{
            flexDirection: "row",
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <LoadingButton
            loading={fileUploadLoading}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              marginInlineEnd: "16px",
              my: 1.5,
            }}
            variant="contained"
            component="label"
          >
            {t("importOrdersDialog.choose")}
            <InputExcelFile
              // order="task-orders-excel-file"
              name="base64"
              onUploadStart={() => {
                setFileUploadLoading(true);
              }}
              onUploadEnded={() => {
                setFileUploadLoading(false);
              }}
              handleChange={handleChange}
            />
          </LoadingButton>
          {!!base64 && (
            <Box display="flex" alignItems="center" mt={2} pb={2}>
              <CheckCircleOutline color="success" sx={{ mr: 2 }} />
              <Typography color="success.main">{t("File is ready")}</Typography>
            </Box>
          )}
        </Box>
        <Autocomplete
          fullWidth
          getOptionLabel={(option) =>
            option && option.name ? option.name : ""
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, newValue) => setSelectedWarehouse(newValue)}
          disableClearable
          value={selectedWarehouse}
          size="small"
          loading={warehousesLoading}
          loadingText={t("loading data...")}
          options={warehouses?.docs ?? []}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(`Select warehouse`)}
              error={Boolean(error)}
              helperText={error}
            />
          )}
        />
        {
          FEDEX_IDS.includes(profile?.merchant?.id) && (
            <Autocomplete
              fullWidth
              sx={{ my: 2 }}
              getOptionLabel={(option) =>
                option && option.name ? option.name : ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, newValue) => setSelectedDriver(newValue)}
              disableClearable
              value={selectedDriver}
              size="small"
              loading={driversLoading}
              loadingText={t("loading data...")}
              options={drivers?.docs ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(`Assign driver`)}
                // error={Boolean(error)}
                // helperText={error}
                />
              )}
            />
          )
          // :
          // <FormControl sx={{ mt: 2 }}>
          //     <FormLabel id="auto-assigned">{t("Driver auto-assign")}</FormLabel>
          //     <RadioGroup
          //         aria-labelledby="auto-assigned"
          //         name="autoAssignDriver"
          //         value={autoAssignDriver}
          //         onChange={(event) => setAutoAssignDriver(event.target.value)}
          //     >
          //         <FormControlLabel value="AUTOMATIC" control={<Radio />} label={t("Assign driver automatically for delivering to customer.")} />
          //         <FormControlLabel value="MANUAL" control={<Radio />} label={t("Just enter orders into a warehouse.")} />
          //     </RadioGroup>
          // </FormControl>
        }
        {!!importedOrdersStatus?.ordersCount && (
          <Box mt={2}>
            <Box display="flex">
              <Typography
                sx={{ mr: 2 }}
              >{`${t("Added orders")} : `}</Typography>
              <Typography color="primary">
                {importedOrdersStatus?.ordersCount ?? "-"}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography
                sx={{ mr: 2 }}
              >{`${t("Orders count that are assigned to the drivers")} : `}</Typography>
              <Typography color="primary">
                {importedOrdersStatus?.assignedForDropOffOrdersCount ?? "-"}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography
                sx={{ mr: 2 }}
              >{`${t("Unassigned orders in the warehouse")} : `}</Typography>
              <Typography color="primary">
                {importedOrdersStatus?.inWarehouseOrdersCount ?? "-"}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row-reverse",
        }}
      >
        <Box>
          <LoadingButton
            variant="contained"
            onClick={closeDialogHandler}
            sx={{ textTransform: "none" }}
            disabled={isSuccess === true}
            loading={loading}
          >
            {t("importOrdersDialog.cancel")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            sx={{ textTransform: "none", marginInlineEnd: "16px", mx: 1 }}
            onClick={confirmHandler}
            disabled={disabledConfirm}
            loading={loading}
            disableElevation
          >
            {t("importOrdersDialog.confirm")}
          </LoadingButton>
        </Box>
        {!!error && <Typography color="error">{t(error)}</Typography>}
        {isSuccess && !importedOrdersStatus?.errors && (
          <Typography color="green.main">{t("Done successfully")}</Typography>
        )}
      </DialogActions>
    </Dialog>
  );
}
